import { Button, Modal, useToast } from '@ethsign/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { SingPassIcon } from '@/components';
import { EVENT_MESSAGE_NAME_ENUM } from '@/constants';
import { useUserInfo } from '@/hooks/useUserInfo';
import { connectSingpass, disconnectSingpass, prepareSingpass } from '@/services';
import { eventBus } from '@/utils';

export const SingpassConnect = ({ isPrimary }: { isPrimary: boolean }) => {
  const { toast } = useToast();
  const { user, fetchUser } = useUserInfo();
  const [open, setOpen] = useState(false);
  const ref = useRef<Window | null>(null);
  const { mutate: hanldeDisConnect, isPending: disConnectLoading } = useMutation({
    mutationKey: ['disConnectSingpass'],
    mutationFn: disconnectSingpass,
    onSuccess: () => {
      fetchUser();
      toast({
        variant: 'success',
        title: 'Success',
        description: 'SingPass disconnected'
      });
    }
  });

  const { mutate: hanldeConnect, isPending: connectLoading } = useMutation({
    mutationKey: ['connectSingpass'],
    mutationFn: connectSingpass,
    onSuccess: () => {
      fetchUser();
      toast({
        title: 'Success',
        description: 'SingPass connected',
        variant: 'success'
      });
    }
  });

  useEffect(() => {
    const handleSingpassConnect = async (event: any) => {
      const { data, origin } = event;
      if (origin === window.location.origin) {
        ref.current?.close();
        if (!data.payload) {
          toast({
            title: 'Error',
            description: "Couldn't connect to SingPass",
            variant: 'error'
          });
        } else {
          await hanldeConnect({
            code: data.payload?.code
          });
        }
      }
    };
    eventBus.on(EVENT_MESSAGE_NAME_ENUM.SINGPASS_CONNECT_SUCCESS, handleSingpassConnect);

    return () => {
      eventBus.off(EVENT_MESSAGE_NAME_ENUM.SINGPASS_CONNECT_SUCCESS, handleSingpassConnect);
    };
  }, []);

  const connectSingPass = async () => {
    const configData = await prepareSingpass();
    const authorizeUrl = `${configData.authorizeApiUrl}?client_id=${configData.clientId}&scope=${configData.scope}&purpose_id=${configData.purposeId}&code_challenge=${configData.codeChallenge}&code_challenge_method=S256&redirect_uri=${configData.redirectUrl}`;

    const singPassWindow = window.open(authorizeUrl, 'singpass', 'width=500,height=500')!;

    ref.current = singPassWindow;
  };

  return (
    <div className="flex items-center justify-between py-4 px-6">
      <div className="flex items-center">
        <div className={'w-8 mr-3'}>
          <SingPassIcon />
        </div>
        <div>
          <div className="text-sm text-gray-900">
            SingPass{user?.singpassUserId ? `: ${user.singpassUserName}(*${user.singpassUserId.slice(-4)})` : ''}{' '}
            {isPrimary && (
              <span className="text-tangerine-700 font-medium text-xs px-2 py-[2x] rounded-full bg-tangerine-50">
                Primary
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400">
            Connect your account with Singapore residents&apos; trusted digital identity.
          </div>
        </div>
      </div>
      <div className={'ml-2'}>
        {user?.singpassUserId ? (
          <Modal
            open={open}
            onOpenChange={setOpen}
            header={
              <>
                <div className="text-lg font-medium text-gray-900">Disconnet SingPass </div>
              </>
            }
            trigger={<Button variant="secondary">Disconnect</Button>}
            onCancel={() => setOpen(false)}
            onConfirm={hanldeDisConnect}
            confirmButtonProps={{
              loading: disConnectLoading,
              variant: 'primary'
            }}
          >
            <div>
              Are you sure you want to disconnect your SingPass account? This will remove your SingPass identity from
              your account.
            </div>
          </Modal>
        ) : (
          <Button onClick={connectSingPass} loading={connectLoading}>
            Connect
          </Button>
        )}
      </div>
    </div>
  );
};
